import { all, prefersReducedMotion } from '../lib/dom';

/**
 * Background parallax on cover blocks — replaces the `rellax` dependency.
 *
 * Each `.wp-block-cover.has-parallax` gets an oversized child that inherits the
 * cover's background (`.wp-block-cover .rellax` in theme.min.css stretches it
 * 150px past both edges and sets `will-change: transform`). Scrolling translates
 * that child inside the cover's overflow, so the background drifts against the page.
 *
 * The offset math below is a direct port of rellax's, for `speed: 3, center: true`:
 *
 *   base    = speed * 50                                    (percentage pinned to 0.5)
 *   percent = (scrollY - blockTop + viewportH) / (blockH + viewportH)
 *   offset  = speed * 100 * (1 - percent) - base
 *
 * so the element sits exactly where the HTML puts it when centred in the viewport.
 */

const SPEED = 3;
const BASE = SPEED * 50;

interface Block {
  el: HTMLElement;
  top: number;     // absolute document offset of the layer
  height: number;
}

export function parallax(): void {
  const covers = all<HTMLElement>('.wp-block-cover.has-parallax');
  if (covers.length === 0) return;

  const blocks: Block[] = [];

  for (const cover of covers) {
    const layer = document.createElement('div');
    layer.className = 'rellax';
    layer.setAttribute('aria-hidden', 'true');
    cover.prepend(layer);
    blocks.push({ el: layer, top: 0, height: 0 });
  }

  // Reduced motion: the layer still renders the background, it just never moves.
  if (prefersReducedMotion()) return;

  let viewportH = window.innerHeight;
  let ticking = false;

  const measure = (): void => {
    viewportH = window.innerHeight;
    const scrollY = window.scrollY;
    for (const block of blocks) {
      const rect = block.el.getBoundingClientRect();
      block.top = scrollY + rect.top;
      block.height = block.el.clientHeight || block.el.offsetHeight;
    }
  };

  const render = (): void => {
    ticking = false;
    const scrollY = window.scrollY;
    for (const block of blocks) {
      const percent = (scrollY - block.top + viewportH) / (block.height + viewportH);
      const offset = Math.round((SPEED * 100 * (1 - percent) - BASE) * 100) / 100;
      block.el.style.transform = `translate3d(0, ${offset}px, 0)`;
    }
  };

  const onScroll = (): void => {
    // Coalesce bursts of scroll events into one write per frame. rellax instead
    // polled with a permanent rAF loop that ran even when nothing had scrolled.
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(render);
  };

  measure();
  render();

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', () => {
    measure();
    render();
  });

  // Images and fonts settling can shift layout after first paint.
  window.addEventListener('load', () => {
    measure();
    render();
  });
}
